/**
 * The transient pill above the board: "Your turn" (auto-hides after ~2.4s)
 * and "Game on! · Alex goes first" at the start of a game. Purely visual —
 * the same news is announced through the live region by game/sync.ts.
 */
import { useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useUiStore, type Banner } from '../state/uiStore';
import { IconSparkle } from './Icons';
import './TurnBanner.css';

const VISIBLE_MS: Record<Banner['kind'], number> = { turn: 2400, start: 3200 };

export function TurnBanner() {
  const banner = useUiStore((s) => s.banner);
  const hideBanner = useUiStore((s) => s.hideBanner);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => hideBanner(banner.id), VISIBLE_MS[banner.kind]);
    return () => clearTimeout(timer);
  }, [banner, hideBanner]);

  return (
    <div className="turn-banner-slot" aria-hidden="true">
      <AnimatePresence>
        {banner && (
          <motion.div
            key={banner.id}
            className={clsx('turn-banner', `turn-banner--${banner.kind}`)}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: -16, scale: 0.94 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0, transition: { duration: 0.12 } } : { opacity: 0, y: -10, scale: 0.97, transition: { duration: 0.18 } }}
            // Reduced motion: a plain fade of ≤ 120 ms (a spring on opacity alone runs ~0.4 s).
            transition={reduced ? { duration: 0.12 } : { type: 'spring', stiffness: 420, damping: 30 }}
          >
            {banner.kind === 'start' ? <IconSparkle /> : <span className="turn-banner__dot" />}
            <strong className="turn-banner__title">{banner.title}</strong>
            {banner.detail && <span className="turn-banner__detail">{banner.detail}</span>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
